import { useEffect, useState } from 'react';
import {
  useNavigation,
  useRoute,
  useFocusEffect,
} from '@react-navigation/native';
import { useCallback } from 'react';
import { logger } from '../utils/logger';

/**
 * Hook to track the current navigation state and active route
 */
export function useNavigationState() {
  const navigation = useNavigation();
  const route = useRoute();
  const [activeRoute, setActiveRoute] = useState('home');

  // Extract the current active route from navigation state
  const getCurrentRoute = useCallback(() => {
    try {
      const state = navigation.getState();

      // Navigate through the state to find the active route
      if (state?.routes) {
        const mainRoute = state.routes.find(r => r.name === 'main');
        if (mainRoute?.state?.routes && mainRoute.state.index !== undefined) {
          const tabRoute = mainRoute.state.routes[mainRoute.state.index];
          if (tabRoute?.state?.routes && tabRoute.state.index !== undefined) {
            const stackRoute = tabRoute.state.routes[tabRoute.state.index];
            return {
              tab: tabRoute?.name || 'home',
              screen: stackRoute?.name || 'home',
              params: stackRoute?.params,
            };
          }
          return {
            tab: tabRoute?.name || 'home',
            screen: tabRoute?.name || 'home',
            params: tabRoute?.params,
          };
        }
      }

      return {
        tab: 'home',
        screen: 'home',
        params: undefined,
      };
    } catch (error) {
      logger.warn('Error getting current route:', error);
      return {
        tab: 'home',
        screen: 'home',
        params: undefined,
      };
    }
  }, [navigation]);

  // Update active route when navigation state changes
  useFocusEffect(
    useCallback(() => {
      const currentRoute = getCurrentRoute();
      setActiveRoute(currentRoute.tab);
    }, [getCurrentRoute])
  );

  // Listen to navigation state changes
  useEffect(() => {
    const unsubscribe = navigation.addListener('state', e => {
      const currentRoute = getCurrentRoute();
      setActiveRoute(currentRoute.tab);
    });

    return unsubscribe;
  }, [navigation, getCurrentRoute]);

  return {
    activeRoute,
    currentRoute: getCurrentRoute(),
    setActiveRoute,
  };
}
